<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <card-title-row
        translation-path="sidebar.usableArea.title"
        :has-icon-column="false"
        :has-buttons="false"
        style="padding-left: 52px"
      >
        <template #subtitle>
          <v-alert
            icon="mdi-information-outline"
            color="primary"
            style="font-size: 12px"
            class="text-left my-5"
            dense
            text
          >
            {{ $i18n.t('sidebar.usableArea.subtitle') }}
          </v-alert>
        </template>
      </card-title-row>
      <sidebar-row :has-icon-column="false" class="pl-3">
        <v-row no-gutters>
          <v-col>
            <v-form v-model="isFormValid" ref="addedAreasForm" class="d-flex flex-column" style="gap: 1.3rem">
              <v-row
                no-gutters
                v-for="(item, index) in layersItems"
                :key="index"
                class="align-center"
                style="gap: 0.5rem; flex-wrap: nowrap"
              >
                <v-col cols="auto" class="checkbox-wrapper" style="width: 32px">
                  <v-checkbox v-model="item.checked" hide-details></v-checkbox>
                </v-col>
                <v-col cols="1" style="min-width: 100px; max-width:100%" class="flex-grow-1 flex-shrink-0">
                  <data-input
                    v-model="item.layer_id"
                    :disabled="item.predefined"
                    @change="handleChangeLayer('addedAreasForm', item.buffer)"
                    v-bind="{
                      ...layerInputBind,
                      rules: [v => !!projectLayers.find(layer => layer.id === v)],
                    }"
                  />
                </v-col>
                <v-col cols="1" style="min-width: 100px" class="flex-grow-0 flex-shrink-1">
                  <data-input v-model="item.buffer" v-bind="getBufferProps(item.layer_id)" />
                </v-col>
                <v-col cols="auto" style="width: 24px">
                  <v-icon v-if="!item.predefined" @click="deleteArea(index)" color="error">mdi-close</v-icon>
                </v-col>
              </v-row>
            </v-form>
            <v-form v-model="isNewAreaValid" lazy-validation ref="newAreaForm" style="margin-top: 1.3rem">
              <v-row no-gutters style="flex-wrap: nowrap; gap: 0.5rem" class="align-center">
                <v-col cols="auto" class="checkbox-wrapper" style="width: 32px"></v-col>
                <v-col cols="1" style="min-width: 100px; max-width:100%" class="flex-grow-1 flex-shrink-0">
                  <data-input
                    v-model="newArea.layer_id"
                    @change="handleChangeLayer('newAreaForm', newArea.buffer)"
                    v-bind="{ ...layerInputBind, rules: [v => !!v] }"
                  />
                </v-col>
                <v-col cols="1" style="min-width: 100px" class="flex-grow-0 flex-shrink-1">
                  <data-input v-model="newArea.buffer" v-bind="getBufferProps(newArea.layer_id)" />
                </v-col>
                <v-col cols="auto" style="width: 24px">
                  <v-icon :color="isNewAreaValid ? 'success' : 'grey'" @click="pushArea">mdi-check</v-icon>
                </v-col>
              </v-row>
            </v-form>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row :has-icon-column="false" style="padding-left: 52px" class="py-7">
        <v-divider></v-divider>
      </sidebar-row>
      <v-form v-model="areAdditionalParamsValid">
        <sidebar-row :has-icon-column="false" style="padding-left: 52px">
          <v-row no-gutters class="align-center" style="gap: 0.8rem">
            <v-col cols="auto" class="text-body-2"> {{ $i18n.t('sidebar.usableArea.innerBuffer') }}: </v-col>
            <v-col cols="3" style="max-width: 85px">
              <data-input
                v-model="innerBuffer"
                :dataType="{ name: 'float' }"
                :hideDetails="true"
                :rules="[v => !!v || v == '0']"
              />
            </v-col>
          </v-row>
        </sidebar-row>
      </v-form>
      <template v-if="isCalculationDone">
        <sidebar-row :has-icon-column="false" style="padding-left: 52px" class="mt-6 ">
          <v-row no-gutters class="align-center" style="gap: 0.5rem">
            <v-col cols="auto">
              <span class="font-weight-medium">{{ $i18n.t('sidebar.usableArea.results') }}</span>
            </v-col>
            <v-col cols="auto">
              <v-menu :attach="true" style="position: relative">
                <template #activator="{ on, attrs }">
                  <base-rounded-button
                    :disabled="!result.geometry"
                    v-on="on"
                    v-bind="attrs"
                    size="x-small"
                    icon="mdi-content-save"
                  ></base-rounded-button>
                </template>
                <v-list class="text-left">
                  <v-list-item @click="saveToKML">
                    <v-list-item-title>{{ $i18n.t('sidebar.usableArea.saveAsKML') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="() => (isDataSourceModalLoaded = isDataSourceModalVisible = true)">
                    <v-list-item-title>{{ $i18n.t('sidebar.usableArea.saveToDatasource') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row :has-icon-column="false" style="padding-left: 52px" class="mt-4">
          <table>
            <tbody>
              <tr v-for="[key, value] in Object.entries(result.values)" :key="key" class="text-left">
                <td class="pr-2 text-body-2">{{ $i18n.t(`sidebar.usableArea.${key}`) }}:</td>
                <td>
                  <v-chip small>{{ value }} ha</v-chip>
                </td>
              </tr>
            </tbody>
          </table>
        </sidebar-row>
      </template>
    </v-col>
    <main-input-dialog
      v-if="isDataSourceModalLoaded"
      :is-visible.sync="isDataSourceModalVisible"
      :loading="saveResultLoading"
      :title-translation="$i18n.t('dialog.title.saveResultToDataSource')"
      dialog-width="30%"
      @agree="saveToDatasource"
      :inputs="dataSourceModalInputs"
    >
      <template #[`dataSourceName.input`]="data">
        <v-col cols="6">
          <data-input v-model="resultDataSourceName" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import { KML, GeoJSON } from 'ol/format';
import { Style, Fill, Stroke } from 'ol/style';

import { get, sync, call } from 'vuex-pathify';

export default {
  name: 'UsableArea',
  components: {
    SidebarRow,
    CardTitleRow,
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    isDataSourceModalLoaded: false,
    isDataSourceModalVisible: false,
    saveResultLoading: false,
    resultDataSourceName: null,
    innerBuffer: null,
    currentTaskId: null,
    result: {
      geometry: null,
      values: {
        areaSum: null,
        usableArea: null,
      },
    },
    isFormValid: false,
    areAdditionalParamsValid: false,
    isNewAreaValid: false,
    isCalculationDone: false,
    newArea: {
      layer_id: null,
      buffer: null,
    },
    layersItems: [],
  }),
  watch: {
    projectLayers: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.$refs['addedAreasForm']?.validate();
        });
      },
    },
    isCalculatingValid(nV) {
      this.isStickyButtonsValid = nV;
    },
    computedStickyButtonTrigger: {
      handler() {
        this.setStickyButtons();
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    projectElements: get('layers/project@layers'),
    currentLayer: get('layers/currentLayer'),
    selectedFeatures: get('map/selectedFeatures@:currentLayerId'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    dataSources: get('layers/metadata'),
    currentLayerId() {
      return this.currentLayer?.id;
    },
    computedStickyButtonTrigger() {
      return this.selectedFeatures | this.isCalculationDone;
    },
    isCurrentLayerPolygon() {
      return ['polygon', 'multipolygon'].includes(this.currentLayer?.geometry_type);
    },
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => ['multipolygon'].includes(dataSource.geometry_type) && dataSource.scope === 'core'
      );
    },
    dataSourceModalInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          subtitle: 'sidebar.buffer.dataSourceSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers.filter(layer => !!layer.geometry_type);
    },
    isCalculatingValid() {
      return (
        this.layersItems.some(item => item.checked) &&
        this.isFormValid &&
        this.areAdditionalParamsValid &&
        this.isCurrentLayerPolygon &&
        this.selectedFeatures?.length > 0 &&
        this.selectedFeatures?.length <= 1000
      );
    },
    layerInputBind() {
      return {
        name: 'layer_id',
        dataType: { name: 'select' },
        items: this.projectLayers,
        itemText: 'name',
        itemValue: 'id',
        hideDetails: true,
        label: this.$i18n.t('sidebar.usableArea.layerToCalculate'),
      };
    },
    bufferInputBind() {
      return {
        name: 'buffer',
        dataType: { name: 'float' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.usableArea.buffer'),
      };
    },
  },
  methods: {
    addFeatureAction: call('layers/addDatasourceFeature'),
    getUsableArea: call('externalSystems/getUsableArea'),
    stopCeleryTask: call('celeryTasks/stopCeleryTask'),
    setStickyButtons() {
      this.stickyButtons = [
        {
          action: 'handleCalculate',
          translationPath: this.isCalculationDone
            ? 'sidebar.usableArea.runCalculationAgain'
            : 'sidebar.usableArea.calculate',
          tooltip: this.selectedFeatures?.length > 0 ? '' : this.$i18n.t('rules.selectObjects'),
        },
        ...(this.isCalculationDone
          ? [
              {
                action: 'resetAnalysis',
                translationPath: 'dialog.reset',
                cancel: true,
              },
            ]
          : []),
      ];
    },
    handleChangeLayer(formId, bufferValue) {
      if (!(bufferValue || bufferValue === 0)) return;
      this.$nextTick(() => {
        this.$refs[formId]?.validate();
      });
    },
    getBufferProps(layerId) {
      const layerGeometry = this.projectLayers.find(layer => layer.id === layerId)?.geometry_type;
      const canGetZeroBuffer = layerGeometry ? ['multipolygon', 'polygon'].includes(layerGeometry) : true;
      return {
        ...this.bufferInputBind,
        rules: [v => (canGetZeroBuffer ? parseFloat(v) >= 0 : !!v)],
      };
    },
    saveToKML() {
      let kmlString = `<?xml version="1.0" encoding="utf-8" ?>${new KML().writeFeatures(
        new GeoJSON().readFeatures(this.result.geometry),
        {
          featureProjection: this.result.geometry.crs.properties.name || this.$_config.defaultEpsg,
          dataProjection: 'EPSG:4326',
        }
      )}`;
      const file = new Blob([kmlString], {
        type: 'text/xml;charset=utf-8;',
      });
      this.$_saveFile(URL.createObjectURL(file), `usableAreaCalc.kml`);
    },
    async saveToDatasource() {
      this.saveResultLoading = true;
      const dataSource = this.dataSources[this.resultDataSourceName];
      const geometry = this.result.geometry;
      const payload = {
        datasource: this.resultDataSourceName,
        body: {
          [dataSource.attributes_schema.geometry_name]: geometry,
        },
      };
      try {
        await this.addFeatureAction(payload);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.measurementsModule.successSaveFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.resultDataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
        this.isDataSourceModalVisible = false;
      } finally {
        this.saveResultLoading = false;
      }
    },
    deleteArea(index) {
      this.layersItems.splice(index, 1);
    },
    handleGeometryInstance(geom) {
      if (!geom) {
        this.$root.$emit('clearVisibleGeomLayerAndFitView', 'usableAreaLayer');
        return;
      }
      this.$root.$emit(
        'createVisibleGeomLayerAndFitView',
        geom,
        'usableAreaLayer',
        {},
        {
          style: new Style({
            fill: new Fill({
              color: 'rgba(200, 0, 0, 0.6)',
            }),
            stroke: new Stroke({
              color: 'rgba(255, 0, 0, 1)',
              width: 2,
            }),
          }),
        }
      );
    },
    pushArea() {
      if (!this.isNewAreaValid) return;
      this.layersItems.push({
        checked: true,
        // TODO 'predefined' (placeholder name) is future attribute for part 2
        predefined: false,
        layer_id: this.newArea.layer_id,
        buffer: this.newArea.buffer,
      });
      this.newArea.layer_id = null;
      this.$refs['newAreaForm']?.resetValidation();
    },
    endCalculationsCallback() {
      this.currentTaskId = null;
      this.isStickyButtonsLoading = false;
    },
    handleUsableAreaTaskResult(r) {
      const resultGeometry = r.geometry && r.geometry.coordinates.length > 0 ? r.geometry : null;
      this.handleGeometryInstance(resultGeometry);
      this.result = {
        geometry: resultGeometry,
        values: {
          areaSum: (r.total_area / 10000).toFixed(2),
          usableArea: r.usable_area ? (r.usable_area / 10000).toFixed(2) : 0,
        },
      };
      localStorage.setItem(
        'usable-area-config',
        JSON.stringify({
          items: this.layersItems,
          innerBuffer: this.innerBuffer,
        })
      );
      this.isCalculationDone = true;
      this.endCalculationsCallback();
    },
    async handleCalculate() {
      this.isCalculationDone = false;
      this.isStickyButtonsLoading = true;
      const payload = {
        internal_data: this.layersItems
          .filter(item => item.checked)
          .map(item => {
            return {
              layer_id: item.layer_id,
              buffer: item.buffer,
            };
          }),
        buffer: this.innerBuffer,
        layer: {
          ids: this.selectedFeatures,
          layer_id: this.currentLayerId,
        },
      };
      try {
        this.currentTaskId = await this.getUsableArea(payload);
        this.$globalHub.$emit('handleCeleryTask', {
          currentTaskId: this.currentTaskId,
          taskSuccessResultParams: {
            type: 'data',
          },
          taskFailureSnackbar: {
            message: this.$i18n.t('snackbar.usableAreaError'),
          },
          taskFailureCallback: this.endCalculationsCallback,
          taskSuccessCallback: r => this.handleUsableAreaTaskResult(r),
        });
      } catch (error) {
        this.endCalculationsCallback();
        console.error(error);
      }
    },
    resetAnalysis() {
      this.handleGeometryInstance(null);
      this.isCalculationDone = false;
    },
  },
  mounted() {
    const config = JSON.parse(localStorage.getItem('usable-area-config') || '{}');
    this.innerBuffer = config.innerBuffer || null;
    this.layersItems = config.items || [];
    this.$root.$off('handleCalculate');
    this.$root.$on('handleCalculate', this.handleCalculate);
    this.$root.$off('resetAnalysis');
    this.$root.$on('resetAnalysis', this.resetAnalysis);
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = this.isCalculatingValid;
    this.setStickyButtons();
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    if (this.currentTaskId) this.stopCeleryTask(this.currentTaskId);
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>

<style lang="scss" scoped>
::v-deep .checkbox-wrapper .v-input--selection-controls {
  margin: 0 !important;
  padding: 0 !important;
}
::v-deep .v-alert__icon {
  align-self: center;
}
</style>
